import React, { useEffect, useRef, useState, useCallback } from "react";
import { FaMicrophone, FaTimes } from "react-icons/fa";
import { edgeVoiceMap, chromeVoiceMap, safariVoiceMap } from "./voiceConfig";

const BACKEND_URL = "https://parkengage.ai/api";
const DEEPGRAM_API_KEY = "YOUR_DEEPGRAM_KEY";
const ELEVENLABS_API_KEY = "YOUR_ELEVENLABS_KEY";
const ELEVENLABS_VOICE_ID = "Xb7hH8MSUJpSbSDYk0k2";

const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

const Orb = ({ onClose }) => {
  const recognitionRef = useRef(null);
  const deepgramSocketRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const [listening, setListening] = useState(false);
  const [sessionId] = useState(() => crypto.randomUUID());

  // =============================
  // BACKEND QUERY
  // =============================
  const handleQuery = useCallback(
    async (question) => {
      if (!question) return;

      if (isIOS) stopDeepgramSTT();
      else recognitionRef.current?.stop();
      setListening(false);

      try {
        const res = await fetch(`${BACKEND_URL}/query`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ question, session_id: sessionId }),
        });
        const data = await res.json();

        // --- Use backend-detected language ---
        const lang = data.lang || "en-US";
        await speak(data.text || "Sorry, I couldn't understand that.", lang);
      } catch (err) {
        console.error("❌ Backend fetch error:", err);
      }
    },
    [sessionId]
  );

  // =============================
  // iOS Deepgram STT
  // =============================
  const startDeepgramSTT = useCallback(async () => {
    try {
      mediaStreamRef.current = await navigator.mediaDevices.getUserMedia({ audio: true });
      const socket = new WebSocket(
        `wss://api.deepgram.com/v1/listen?model=nova&language=en-US&punctuate=true&token=${DEEPGRAM_API_KEY}`
      );
      deepgramSocketRef.current = socket;

      socket.onopen = () => {
        const audioContext = new AudioContext({ sampleRate: 16000 });
        const source = audioContext.createMediaStreamSource(mediaStreamRef.current);
        const processor = audioContext.createScriptProcessor(4096, 1, 1);
        source.connect(processor);
        processor.connect(audioContext.destination);

        processor.onaudioprocess = (e) => {
          if (socket.readyState === 1) {
            const input = e.inputBuffer.getChannelData(0);
            const pcm16 = new Int16Array(input.length);
            for (let i = 0; i < input.length; i++) pcm16[i] = input[i] * 0x7fff;
            socket.send(pcm16);
          }
        };

        setListening(true);
      };

      socket.onmessage = async (msg) => {
        const data = JSON.parse(msg.data);
        const transcript = data?.channel?.alternatives?.[0]?.transcript;
        if (transcript && data.is_final) {
          console.log("🗣️ (iOS Deepgram) You said:", transcript);
          await handleQuery(transcript);
        }
      };

      socket.onclose = () => setListening(false);
    } catch (err) {
      console.error("❌ Deepgram STT error:", err);
    }
  }, [handleQuery]);

  const stopDeepgramSTT = () => {
    deepgramSocketRef.current?.close();
    mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
    setListening(false);
  };

  // =============================
  // Web Speech STT (non-iOS)
  // =============================
  useEffect(() => {
    if (isIOS) {
      startDeepgramSTT();
      return () => stopDeepgramSTT();
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;
    recognition.lang = "en-US";
    recognitionRef.current = recognition;

    recognition.onresult = async (event) => {
      const text = event.results[event.results.length - 1][0].transcript.trim();
      console.log("🗣️ You said:", text);
      await handleQuery(text);
    };

    recognition.onend = () => {
      if (listening) recognition.start();
    };

    recognition.start();
    setListening(true);

    return () => recognition.stop();
  }, [handleQuery, startDeepgramSTT]);

  // =============================
  // TTS
  // =============================
  const speak = async (text, lang) => {
    if (!text) return;

    // ---------- iOS: ElevenLabs ----------
    if (isIOS) {
      const audioRes = await fetch(
        `https://api.elevenlabs.io/v1/text-to-speech/${ELEVENLABS_VOICE_ID}?optimize_streaming_latency=3`,
        {
          method: "POST",
          headers: {
            "xi-api-key": ELEVENLABS_API_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ text, model_id: "eleven_turbo_v2" }),
        }
      );
      const audioData = await audioRes.arrayBuffer();
      const audioContext = new AudioContext();
      const buffer = await audioContext.decodeAudioData(audioData);
      const source = audioContext.createBufferSource();
      source.buffer = buffer;
      source.connect(audioContext.destination);
      source.start();
      source.onended = () => startDeepgramSTT();
      return;
    }

    // ---------- Web Speech ----------
    if (!window.speechSynthesis) return;
    if (recognitionRef.current && listening) {
      recognitionRef.current.stop();
      setListening(false);
    }

    const utter = new SpeechSynthesisUtterance(text);
    utter.lang = lang;
    utter.rate = 1;
    utter.pitch = 1;

    const getVoiceRegex = () => {
      const ua = navigator.userAgent.toLowerCase();
      if (ua.includes("edg")) return edgeVoiceMap[lang];
      if (ua.includes("chrome")) return chromeVoiceMap[lang];
      if (ua.includes("safari")) return safariVoiceMap[lang];
      return null;
    };

    const assignVoice = () => {
      const voices = window.speechSynthesis.getVoices();
      const voiceRegex = getVoiceRegex();
      const female =
        (voiceRegex && voices.find((v) => voiceRegex.test(v.name))) ||
        voices.find((v) => v.lang === lang) ||
        voices.find((v) => /female|aria|jenny|zira|hazel|victoria|serena|karen/i.test(v.name)) ||
        voices[0];

      utter.voice = female;
      console.log("🎤 Using Voice:", female?.name);

      utter.onend = () => {
        try {
          recognitionRef.current?.start();
          setListening(true);
        } catch { }
      };

      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    };

    if (window.speechSynthesis.getVoices().length === 0) {
      window.speechSynthesis.onvoiceschanged = assignVoice;
    } else {
      assignVoice();
    }
  };

  // =============================
  // MIC TOGGLE
  // =============================
  const handleMicToggle = () => {
    if (isIOS) listening ? stopDeepgramSTT() : startDeepgramSTT();
    else {
      if (listening) recognitionRef.current?.stop();
      else recognitionRef.current?.start();
      setListening(!listening);
    }
  };

  // =============================
  // UI
  // =============================
  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-white">
      <div
        className={`w-48 h-48 rounded-full transition-all ${listening ? "animate-pulse scale-105" : "scale-100"
          }`}
        style={{
          background: "linear-gradient(135deg, #5CB6FF 0%, #B18CFF 100%)",
        }}
      ></div>

      <div className="flex items-center justify-center gap-6 mt-8">
        <button
          onClick={handleMicToggle}
          className={`w-14 h-14 flex items-center justify-center rounded-full text-white text-2xl ${listening ? "bg-red-500 animate-pulse" : "bg-gray-400"
            }`}
        >
          <FaMicrophone />
        </button>

        <button
          onClick={() => {
            // --- Stop all active audio / recognition sessions before closing ---
            try {
              if (isIOS) {
                deepgramSocketRef.current?.close();
                mediaStreamRef.current?.getTracks().forEach((t) => t.stop());
              } else {
                recognitionRef.current?.stop();
              }
              window.speechSynthesis.cancel();
              setListening(false);
            } catch (err) {
              console.error("❌ Error closing session:", err);
            }

            // --- Then trigger parent close ---
            onClose();
          }}
          className="w-14 h-14 flex items-center justify-center rounded-full bg-gray-900 text-white text-2xl"
        >
          <FaTimes />
        </button>

      </div>
    </div>
  );
};

export default Orb;
